import { FlowTypes } from '@interfaces/models/flow-types';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { findStaleMinorDraft } from '@shared/helpers/stale-minor-draft.helper';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import { DraftLookupState, DraftState } from '@store/reducers/draft/draft.reducer';
import {
  selectCwpMinorSaveDraftToggle,
  selectInheritanceSaveDraftInternalToggle,
  selectInheritanceSaveDraftToggle,
} from '@store/selectors/feature/feature.selectors';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';

import { selectDisplayedAccounts } from '../client-accounts/client-accounts.selectors';

export const selectDraftState = createFeatureSelector<DraftState>('draft');

export const selectDraftId = createSelector(selectDraftState, (state) => state.draftId);

export const selectDraftExpDate = createSelector(selectDraftState, (state) => state.expDate);

export const selectDrafts = createSelector(selectDraftState, (state) => state.drafts);

export const selectRawDrafts = createSelector(selectDraftState, (state) => state.rawDrafts);

export const selectDraftsLoading = createSelector(selectDraftState, (state) => state.draftsLoading);

export const selectBaseProcessTrackingId = createSelector(
  selectDraftState,
  (state) => state.baseProcessTrackingId,
);

export const selectDraftSequence = createSelector(selectDraftState, (state) => state.draftSequence);

export const selectDraftLookupState = createSelector(
  selectDraftState,
  (state) => state.lookupState,
);

export const selectDraftLookupFailureAcknowledged = createSelector(
  selectDraftState,
  (state) => state.lookupFailureAcknowledged,
);

/**
 * Both failures count as settled, so a failure starts a fresh request instead of blocking the
 * client. RESUMED and AWAITING_DECISION are left out on purpose: the draftId only arrives with
 * hydration, so including either would save a second, orphaned draft.
 */
const SETTLED_LOOKUP_STATES: DraftLookupState[] = [
  DraftLookupState.NONE_FOUND,
  DraftLookupState.DECLINED,
  DraftLookupState.LOOKUP_FAILED,
  DraftLookupState.RESUME_FAILED,
];

export const selectDraftLookupSettled = createSelector(selectDraftLookupState, (lookupState) =>
  SETTLED_LOOKUP_STATES.includes(lookupState),
);

/**
 * Drives the failed-lookup notice. MINOR-gated here, not in the component, because transfer-from
 * is shared with inheritance, which has its own resume UI. LOOKUP_FAILED only — a failed resume
 * is a different problem and gets no notice.
 */
export const selectMinorDraftLookupFailed = createSelector(
  flowTypeSelection,
  selectDraftLookupState,
  // Keeps a dismissed notice dismissed when the client wanders back to this page.
  selectDraftLookupFailureAcknowledged,
  (flowType, lookupState, acknowledged) =>
    flowType === FlowTypes.MINOR && lookupState === DraftLookupState.LOOKUP_FAILED && !acknowledged,
);

/**
 * The flow-entry view of findStaleMinorDraft. `displayedAccounts` and not
 * selectEligibleMinorAccounts, which omits the age-of-termination filter.
 *
 * The redirect path cannot use this selector because it runs while the ineligible page is
 * resetting state. It calls the helper directly. See discardAllMinorDrafts$.
 */
export const selectStaleMinorDraft = createSelector(
  selectDrafts,
  selectRawDrafts,
  selectDisplayedAccounts,
  (drafts, rawDrafts, displayedAccounts) =>
    findStaleMinorDraft(
      drafts,
      rawDrafts,
      displayedAccounts.map((account) => account.accountId),
    ),
);

/**
 * Gate and row come from one selector on purpose — recombining them in the component paired a
 * fresh "prompt now" with a stale empty list. MINOR-gated here rather than in the component
 * because transfer-from is shared with inheritance, which has its own resume UI.
 */
export const selectMinorDraftToResume = createSelector(
  flowTypeSelection,
  selectDraftLookupState,
  selectDrafts,
  selectDraftId,
  selectStaleMinorDraft,
  (flowType, lookupState, drafts, draftId, staleDraft) => {
    if (flowType !== FlowTypes.MINOR || lookupState !== DraftLookupState.AWAITING_DECISION) {
      return null;
    }

    // Never offer a draft to a client who already has one in play — declining that prompt
    // would hard-delete the request they are working on.
    if (draftId) {
      return null;
    }

    if (staleDraft) {
      return null;
    }

    // A client can hold several drafts; filterDrafts sorts newest-first, so offer drafts[0].
    return drafts.length ? drafts[0] : null;
  },
);

// save-draft gating is per-flow.
// Internal and external users are gated independently for multi-inheritance:
//   internal crew → cwp_inheritance_save_draft_internal
//   external users → cwp_inheritance_save_draft
export const selectSaveDraftEnabledForFlow = createSelector(
  flowTypeSelection,
  selectInheritanceSaveDraftToggle,
  selectCwpMinorSaveDraftToggle,
  selectInheritanceSaveDraftInternalToggle,
  (flowType, isInheritanceToggleOn, isMinorToggleOn, isInternalToggleOn) => {
    switch (flowType) {
      case FlowTypes.MINOR:
        return !!isMinorToggleOn;
      case FlowTypes.MULTI_INHERITANCE:
        return getConfigClientFromRTE().isInternal ? !!isInternalToggleOn : !!isInheritanceToggleOn;
      default:
        return false;
    }
  },
);

/**
 * Gates the full-page loading treatment. Not `draftsLoading` — that also flips on the delete
 * path, so it isn't lookup-specific. Includes NOT_CHECKED (not just CHECKING) because the
 * pending window starts at first paint, before RetrieveDrafts is dispatched.
 */
export const selectMinorDraftLookupPending = createSelector(
  flowTypeSelection,
  selectSaveDraftEnabledForFlow,
  selectDraftLookupState,
  (flowType, isSaveDraftEnabled, lookupState) =>
    flowType === FlowTypes.MINOR &&
    isSaveDraftEnabled &&
    (lookupState === DraftLookupState.NOT_CHECKED || lookupState === DraftLookupState.CHECKING),
);
